import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Play, Pause, SkipBack, Columns2, Palette, SlidersHorizontal, AudioLines, Music2, Clapperboard, Check, Loader2, LayoutList, Activity, Share, Sparkles, Box, Sun, Undo2, Redo2, Bot } from "lucide-react";
import { ClipPanel, FX_DEFAULT } from "@/components/editor/ClipPanel";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/auth/AuthLayout";
import { MediaLibrary } from "@/components/editor/MediaLibrary";
import { Viewer } from "@/components/editor/Viewer";
import { Timeline } from "@/components/editor/Timeline";
import { MixerConsole } from "@/components/editor/MixerConsole";
import { ColourPanel } from "@/components/editor/ColourPanel";
import { SoundPanel } from "@/components/editor/SoundPanel";
import { SfxPanel } from "@/components/editor/SfxPanel";
import { ComposerPanel } from "@/components/editor/ComposerPanel";
import { AnimationPanel } from "@/components/editor/AnimationPanel";
import { Scopes } from "@/components/editor/Scopes";
import { ExportDialog } from "@/components/editor/ExportDialog";
import { useHistory } from "@/hooks/useHistory";
import { AgentPanel } from "@/components/editor/AgentPanel";
import { runActions, projectState } from "@/lib/agentActions";
import { applySuggestion } from "@/components/editor/AutoMixCard";
import { layout } from "@/lib/layout";
import { HoverHelpToggle } from "@/components/HoverHelpToggle";
import { sliceAud } from "@/lib/clipAudio";
import { LightPanel } from "@/components/editor/LightPanel";
import { VfxPanel } from "@/components/editor/VfxPanel";
import { useTransport } from "@/hooks/useTransport";
import { AudioEngine, mergeMixer } from "@/lib/audioEngine";
import { mergeGrade } from "@/lib/grade";
import { kfSet, layerAt, migrateFx, pathPoints, sliceKfs } from "@/lib/layerMotion";
import { api, formatApiError, formatTime, fileUrl } from "@/lib/api";

const trackFor = (kind) => (["footage", "animation", "export"].includes(kind) ? "video" : kind === "vfx" ? "fx" : kind === "mixdown" ? "audio" : kind);
const TABS = [["colour", "Colour", Palette], ["sound", "Mix", SlidersHorizontal], ["sfx", "SFX", AudioLines], ["vfx", "VFX", Sparkles], ["cgi", "CGI", Box], ["score", "Score", Music2], ["animate", "Animate", Clapperboard], ["light", "Light", Sun]];

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [assets, setAssets] = useState([]);
  const [grade, setGrade] = useState(mergeGrade());
  const [clips, setClips] = useState([]);
  const [mixer, setMixer] = useState(mergeMixer());
  const [saveState, setSaveState] = useState("saved");
  const [compare, setCompare] = useState(false);
  const [dock, setDock] = useState("timeline");
  const [scopes, setScopes] = useState(true);
  const [bin, setBin] = useState("all");
  const [exportOpen, setExportOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const canvasRef = useRef(null);
  const fxRef = useRef([]);
  const videoBRef = useRef(null);
  const viewRef = useRef({});
  const [selected, setSelected] = useState(null);
  const [, bump] = useState(0);
  const videoRef = useRef(null);
  const loaded = useRef(false);
  const engine = useMemo(() => new AudioEngine(), []);
  const history = useHistory({ grade, clips, mixer }, (s) => { setGrade(s.grade); setClips(s.clips); setMixer(s.mixer); });
  const historyRef = useRef(history);
  historyRef.current = history;

  const loadAssets = useCallback(() => api.get(`/projects/${id}/assets`).then((r) => setAssets((prev) => {
    const local = prev.filter((a) => a.status === "uploading" && !r.data.find((x) => x.id === a.id));
    return [...local, ...r.data.map((x) => (x.status === "uploading" ? prev.find((p) => p.id === x.id) || x : x))];
  })), [id]);

  useEffect(() => {
    Promise.all([api.get(`/projects/${id}`), loadAssets()]).then(([p]) => {
      const snap = { grade: mergeGrade(p.data.grade), clips: p.data.clips || [], mixer: mergeMixer(p.data.mixer) };
      setProject(p.data); setGrade(snap.grade); setClips(snap.clips); setMixer(snap.mixer);
      historyRef.current.reset(snap);
      setTimeout(() => (loaded.current = true), 0);
    }).catch((e) => { toast.error(formatApiError(e)); nav("/"); });
  }, [id, loadAssets, nav]);

  const pending = assets.some((a) => a.status === "processing");
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(loadAssets, 4000);
    return () => clearInterval(t);
  }, [pending, loadAssets]);

  useEffect(() => {
    if (!loaded.current) return;
    setSaveState("saving");
    const t = setTimeout(() => api.put(`/projects/${id}`, { grade, clips, mixer }).then(() => setSaveState("saved")).catch(() => setSaveState("error")), 800);
    return () => clearTimeout(t);
  }, [grade, clips, mixer, id]);

  useEffect(() => engine.setMixer(mixer), [mixer, engine]);
  useEffect(() => { if (selected && !clips.find((c) => c.id === selected)) setSelected(null); }, [clips, selected]);

  const assetsMap = useMemo(() => Object.fromEntries(assets.map((a) => [a.id, a])), [assets]);

  const probed = useRef(new Set());
  useEffect(() => {
    clips.forEach((c) => {
      const a = assetsMap[c.asset_id];
      if (!a || a.status !== "ready") return;
      if (c.track === "video" || c.track === "fx") {
        if (a.duration || probed.current.has(a.id)) return;
        probed.current.add(a.id);
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => isFinite(v.duration) && api.patch(`/assets/${a.id}`, { duration: v.duration }).then((r) => setAssets((l) => l.map((x) => (x.id === a.id ? r.data : x))));
        v.src = fileUrl(a.id);
        return;
      }
      if (engine.ready.has(a.id)) return;
      engine.load(a.id).then((buf) => {
        bump((n) => n + 1);
        if (!a.duration) api.patch(`/assets/${a.id}`, { duration: buf.duration }).then((r) => setAssets((l) => l.map((x) => (x.id === a.id ? r.data : x))));
      }).catch(() => toast.error(`Could not decode ${a.name}`));
    });
  }, [clips, assetsMap, engine]);

  const fullDuration = (c) => assetsMap[c.asset_id]?.duration || engine.ready.get(c.asset_id)?.duration || 5;
  let acc = 0, vi = 0, prevLen = 0;
  const laid = clips.map((c) => {
    const full = fullDuration(c);
    const inn = Math.min(c.in ?? 0, full - 0.1);
    const out = Math.min(c.out ?? full, full);
    const r = { ...c, in: inn, out, len: Math.max(0.1, out - inn) };
    if (c.track === "video") {
      const d = vi > 0 && c.trans && c.trans.type !== "none" ? Math.min(c.trans.dur, prevLen * 0.9, r.len * 0.9) : 0;
      r.start = acc - d; r.transDur = d; r.idx = vi++;
      acc = r.start + r.len; prevLen = r.len;
    }
    if (c.track === "fx") r.fx = migrateFx({ ...FX_DEFAULT, ...(c.fx || {}) }, r.len);
    return r;
  });
  const duration = Math.max(10, ...laid.map((c) => c.start + c.len));
  const transport = useTransport({ videoRef, videoBRef, fxRef, viewRef, engine, clips: laid, duration });
  const selClip = laid.find((c) => c.id === selected);
  const selLt = selClip ? Math.min(selClip.len, Math.max(0, transport.time - selClip.start)) : 0;
  const showFxBox = selClip?.track === "fx" && transport.time >= selClip.start && transport.time < selClip.start + selClip.len;

  const addToTimeline = (asset, asLayer = false) => {
    const track = asLayer ? "fx" : trackFor(asset.kind);
    const clip = { id: crypto.randomUUID(), asset_id: asset.id, track, start: track === "video" ? 0 : +transport.time.toFixed(2) };
    if (track === "fx") clip.fx = { ...FX_DEFAULT, blend: asLayer ? "chroma" : asset.meta?.blend || "screen" };
    setClips((cs) => [...cs, clip]);
    if (track === "fx") setSelected(clip.id);
    toast.success(`Added to ${track === "video" ? "picture track" : track === "fx" ? "V2 layer track" : `${track} track`}`);
  };
  const patchClip = (cid, patch) => setClips((cs) => cs.map((c) => (c.id === cid ? { ...c, ...Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, typeof v === "number" ? +v.toFixed(3) : v])) } : c)));
  const reorderClip = (cid, idx) => setClips((cs) => {
    const moving = cs.find((c) => c.id === cid);
    const vids = cs.filter((c) => c.track === "video" && c.id !== cid);
    vids.splice(idx, 0, moving);
    return [...vids, ...cs.filter((c) => c.track !== "video")];
  });
  const splitClip = (selectedId) => {
    const t = transport.time;
    const under = laid.filter((c) => t > c.start + 0.05 && t < c.start + c.len - 0.05);
    const c = under.find((x) => x.id === selectedId) || under.find((x) => x.track === "video") || under[0];
    if (!c) return toast.error("Place the playhead over a clip to blade it");
    const cut = +(c.in + (t - c.start)).toFixed(3);
    setClips((cs) => cs.flatMap((x) => x.id !== c.id ? [x] : [
      { ...x, in: c.in, out: cut, ...(c.fx && { fx: { ...c.fx, ...sliceKfs(c.fx, 0, t - c.start) } }), ...(c.aud && { aud: sliceAud(c.aud, 0, t - c.start, "left") }) },
      { ...x, id: crypto.randomUUID(), in: cut, out: c.out, trans: undefined, start: x.track === "video" ? 0 : +t.toFixed(3),
        ...(c.fx && { fx: { ...c.fx, ...sliceKfs(c.fx, t - c.start, c.len) } }), ...(c.aud && { aud: sliceAud(c.aud, t - c.start, c.len, "right") }) },
    ]));
  };
  const onCreated = (a) => setAssets((l) => [a, ...l]);
  const deleteAsset = async (a) => {
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    try { await api.delete(`/assets/${a.id}`); } catch (e) { return toast.error(formatApiError(e)); }
    setAssets((l) => l.filter((x) => x.id !== a.id));
    setClips((cs) => cs.filter((c) => c.asset_id !== a.id));
  };

  const live = useRef({});
  live.current = { clips, grade, mixer, assets, laid, time: transport.time, duration, fullDuration };
  const agentSnaps = useRef({});
  const runJob = async (job, mid) => {
    const undone = () => mid && !agentSnaps.current[mid];
    try {
      if (job.type === "light") {
        const { data } = await api.post("/generate/light", { project_id: id, preset: job.preset, duration: job.duration });
        onCreated(data);
        if (undone()) return;
        setClips((cs) => [...cs, { id: crypto.randomUUID(), asset_id: data.id, track: "fx", start: job.start, fx: { ...FX_DEFAULT, blend: "screen" } }]);
      }
      if (job.type === "automix") {
        const L = layout(live.current.clips, live.current.fullDuration).filter((c) => ["video", "audio", "sfx", "score"].includes(c.track));
        if (!L.length) return toast.error("Auto mix needs audio on the timeline");
        const { data } = await api.post("/mix/auto", { project_id: id, style: job.style,
          clips: L.map((c) => ({ asset_id: c.asset_id, track: c.track, in_point: c.in, out_point: c.out, start: c.start })) });
        for (;;) {
          await new Promise((res) => setTimeout(res, 1500));
          const s = (await api.get(`/mix/auto/${data.id}`)).data;
          if (s.status === "failed") return toast.error(s.error || "Auto mix failed");
          if (undone()) return;
          if (s.status === "ready") { setMixer((m) => applySuggestion(m, s.result)); return toast.success("AI Editor applied the auto mix"); }
        }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const undoAgent = (mid) => {
    const s = agentSnaps.current[mid];
    if (!s) return history.undo();
    setClips(s.clips); setGrade(s.grade); setMixer(s.mixer);
    delete agentSnaps.current[mid];
    toast.success("Reverted the AI Editor's changes");
  };
  const runAgent = (actions, mid) => {
    const L = live.current;
    agentSnaps.current[mid] = { clips: L.clips, grade: L.grade, mixer: L.mixer };
    const res = runActions({ clips: L.clips, grade: L.grade, mixer: L.mixer }, actions, { assets: L.assets, fullDuration: L.fullDuration, time: L.time });
    setClips(res.clips); setGrade(res.grade); setMixer(res.mixer);
    if (res.seek != null) setTimeout(() => transport.seek(res.seek), 50);
    res.jobs.forEach((j) => runJob(j, mid));
    return res.log;
  };
  const agentState = () => projectState({ laid: live.current.laid, assets: live.current.assets, grade: live.current.grade, mixer: live.current.mixer, time: live.current.time, duration: live.current.duration });

  const keyRef = useRef({});
  keyRef.current = { transport, splitClip, history };
  useEffect(() => {
    const onKey = (e) => {
      const typing = e.target.tagName === "TEXTAREA" || (e.target.tagName === "INPUT" && e.target.type !== "range");
      const mod = e.metaKey || e.ctrlKey, k = e.key.toLowerCase();
      if (mod && !typing && (k === "z" || k === "y")) {
        e.preventDefault();
        return k === "y" || e.shiftKey ? keyRef.current.history.redo() : keyRef.current.history.undo();
      }
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.code === "Space") { e.preventDefault(); keyRef.current.transport.toggle(); }
      if (e.key === "b" || e.key === "B") keyRef.current.splitClip(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!project) return <div className="h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>;

  const common = { projectId: id, assets, onCreated, onAdd: addToTimeline };

  return (
    <div className="h-screen flex flex-col bg-[#0B0C10] overflow-hidden" data-testid="editor">
      <header className="h-14 bg-[#0B0C10]/95 border-b border-[#2A2E3D] px-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => nav("/")} className="text-slate-400 hover:text-white" data-testid="back-to-dashboard"><ArrowLeft className="w-4 h-4" /></button>
          <Logo />
          <span className="text-slate-600">/</span>
          <span className="font-display font-medium" data-testid="editor-project-name">{project.name}</span>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => setAgentOpen((o) => !o)} data-testid="agent-open-button" aria-pressed={agentOpen}
            className={`flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border transition-[box-shadow,background-color] ${agentOpen ? "bg-[#FFB800]/20 border-[#FFB800] text-[#FFB800]" : "border-[#FFB800]/50 text-[#FFB800] hover:shadow-[0_0_14px_rgba(255,184,0,0.35)]"}`}>
            <Bot className="w-4 h-4" />AI Editor
          </button>
          <HoverHelpToggle />
          <div className="flex items-center rounded-md border border-[#2A2E3D] overflow-hidden" data-testid="history-controls">
            <button onClick={history.undo} disabled={!history.canUndo} title="Undo (Ctrl/Cmd+Z)" data-testid="undo-button"
              className="px-2.5 py-1.5 text-slate-300 hover:bg-[#1A1D27] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><Undo2 className="w-4 h-4" /></button>
            <button onClick={history.redo} disabled={!history.canRedo} title="Redo (Shift+Ctrl/Cmd+Z)" data-testid="redo-button"
              className="px-2.5 py-1.5 text-slate-300 border-l border-[#2A2E3D] hover:bg-[#1A1D27] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><Redo2 className="w-4 h-4" /></button>
          </div>
          <span className="font-mono-ui text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="save-status">
            {saveState === "saving" ? <><Loader2 className="w-3 h-3 animate-spin" />SAVING</> : saveState === "error" ? <span className="text-red-400">SAVE FAILED</span> : <><Check className="w-3 h-3 text-emerald-400" />SAVED</>}
          </span>
          <button onClick={() => setExportOpen(true)} className="flex items-center gap-2 text-sm font-semibold px-4 py-1.5 rounded-md bg-[#FFB800] text-[#0B0C10] hover:shadow-[0_0_18px_rgba(255,184,0,0.5)] transition-shadow" data-testid="export-video-button">
            <Share className="w-4 h-4" />Export video
          </button>
        </div>
      </header>
      {agentOpen && <AgentPanel projectId={id} getState={agentState} onRun={runAgent} onUndo={undoAgent} onClose={() => setAgentOpen(false)} />}
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} projectId={id} projectName={project.name} grade={grade} clips={laid} mixer={mixer} engine={engine}
        onCreated={(a) => { onCreated(a); setBin("exports"); }} />
      <div className="flex flex-1 min-h-0">
        <MediaLibrary projectId={id} assets={assets} setAssets={setAssets} onAdd={addToTimeline} onAddLayer={(a) => addToTimeline(a, true)} onDelete={deleteAsset} bin={bin} setBin={setBin} />
        <main className="flex-1 flex flex-col min-w-0 p-3 gap-2">
          <div className="flex-1 min-h-0 flex gap-3">
            <div className="flex-1 min-w-0"><Viewer hasClips={laid.some((c) => c.track === "video")} grade={grade} compare={compare} videoRef={videoRef} videoBRef={videoBRef} fxRef={fxRef} viewRef={viewRef} canvasRef={canvasRef}
              selectedFx={showFxBox ? layerAt(selClip.fx, selLt) : null} path={showFxBox ? pathPoints(selClip.fx, selClip.len) : []} keyPoints={showFxBox ? (selClip.fx.kfs || []).map((k) => (k.t <= selClip.len + 0.02 ? k : null)) : []} onKfSeek={(i) => transport.seek(selClip.start + Math.min(selClip.fx.kfs[i].t, selClip.len - 0.01))} onKfMove={(i, xy) => patchClip(selClip.id, { fx: { ...selClip.fx, kfs: selClip.fx.kfs.map((k, j) => (j === i ? { ...k, ...xy } : k)) } })} onFxChange={(patch) => patchClip(selClip.id, { fx: { ...selClip.fx, ...kfSet(selClip.fx, +selLt.toFixed(3), patch) } })} /></div>
            {scopes && <Scopes canvasRef={canvasRef} />}
          </div>
          <div className="flex items-center justify-between px-1">
            <span className="font-mono-ui text-sm text-[#FFB800] w-44" data-testid="timecode">{formatTime(transport.time)}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => transport.seek(0)} className="p-2 rounded-full hover:bg-white/5 text-slate-300" data-testid="transport-rewind"><SkipBack className="w-4 h-4" /></button>
              <button onClick={transport.toggle} className="p-3 rounded-full bg-[#FFB800] text-[#0B0C10] hover:scale-105 transition-transform" data-testid="transport-play-toggle">
                {transport.playing ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4" fill="currentColor" />}
              </button>
            </div>
            <div className="flex gap-1 w-44 justify-end">
              <button onClick={() => setScopes((s) => !s)} className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded transition-colors ${scopes ? "bg-[#00E5FF]/15 text-[#00E5FF]" : "text-slate-400 hover:bg-white/5"}`} data-testid="scopes-toggle">
                <Activity className="w-3.5 h-3.5" />Scopes
              </button>
              <button onClick={() => setCompare((c) => !c)} className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded transition-colors ${compare ? "bg-[#FFB800]/15 text-[#FFB800]" : "text-slate-400 hover:bg-white/5"}`} data-testid="compare-toggle">
                <Columns2 className="w-3.5 h-3.5" />Before/After
              </button>
            </div>
          </div>
        </main>
        <aside className="w-80 xl:w-96 bg-[#13151C] border-l border-[#2A2E3D] flex flex-col min-h-0" data-testid="inspector">
          {selClip && ["fx", "video"].includes(selClip.track) && (
            <ClipPanel clip={selClip} lt={selLt} onSeek={transport.seek} name={assetsMap[selClip.asset_id]?.name || "Clip"} onPatch={(p) => patchClip(selClip.id, p)}
              onRemove={() => { setClips((cs) => cs.filter((c) => c.id !== selClip.id)); setSelected(null); }} onClose={() => setSelected(null)} />
          )}
          <Tabs defaultValue="colour" className="flex flex-col min-h-0 flex-1">
            <TabsList className="grid grid-cols-8 m-2 bg-[#0B0C10] h-auto p-1 shrink-0">
              {TABS.map(([v, l, Icon]) => (
                <TabsTrigger key={v} value={v} className="flex flex-col gap-0.5 py-1.5 text-[10px] data-[state=active]:bg-[#1E222D] data-[state=active]:text-[#FFB800]" data-testid={`inspector-tab-${v}`}>
                  <Icon className="w-3.5 h-3.5" />{l}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex-1 overflow-y-auto">
              <TabsContent value="colour" className="m-0"><ColourPanel grade={grade} setGrade={setGrade} projectName={project.name} /></TabsContent>
              <TabsContent value="sound" className="m-0"><SoundPanel engine={engine} mixer={mixer} setMixer={setMixer} clips={laid} assetsMap={assetsMap} duration={duration} projectName={project.name} openConsole={() => setDock("mixer")} /></TabsContent>
              <TabsContent value="sfx" className="m-0"><SfxPanel {...common} /></TabsContent>
              <TabsContent value="cgi" className="m-0"><VfxPanel {...common} cgi /></TabsContent>
              <TabsContent value="vfx" className="m-0"><VfxPanel {...common} /></TabsContent>
              <TabsContent value="score" className="m-0"><ComposerPanel {...common} /></TabsContent>
              <TabsContent value="animate" className="m-0"><AnimationPanel {...common} /></TabsContent>
              <TabsContent value="light" className="m-0"><LightPanel {...common} /></TabsContent>
            </div>
          </Tabs>
        </aside>
      </div>
      <section className="h-[300px] bg-[#181A22] border-t border-[#2A2E3D] flex flex-col shrink-0" data-testid="bottom-dock">
        <div className="flex gap-1 px-2 pt-1.5 shrink-0">
          {[["timeline", "Timeline", LayoutList], ["mixer", "Mixing console", SlidersHorizontal]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setDock(k)} data-testid={`dock-tab-${k}`}
              className={`flex items-center gap-1.5 text-[11px] px-3 py-1 rounded-t ${dock === k ? "bg-[#0B0C10]/60 text-[#FFB800]" : "text-slate-500 hover:text-slate-300"}`}><Icon className="w-3 h-3" />{l}</button>
          ))}
        </div>
        <div className="flex-1 min-h-0 bg-[#0B0C10]/60">
          {dock === "timeline" ? (
            <Timeline clips={laid} assetsMap={assetsMap} fullDuration={fullDuration} duration={duration} time={transport.time} onSeek={transport.seek}
              onChange={patchClip} onReorder={reorderClip} onSplit={splitClip}
              onRemove={(cid) => setClips((cs) => cs.filter((c) => c.id !== cid))} mixer={mixer} setMixer={setMixer} selected={selected} setSelected={setSelected} />
          ) : <MixerConsole mixer={mixer} setMixer={setMixer} engine={engine} projectId={id} clips={laid} />}
        </div>
      </section>
    </div>
  );
}
